import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Download, FileSpreadsheet, FileText, FileType } from "lucide-react";

const SERVICES = ["ITR", "GST", "MSME Loan", "Insurance", "Other"];
const STATUSES = ["New Case", "Work In Progress", "Completed", "Rejected", "On Hold"];

export default function Reports() {
  const [period, setPeriod] = useState("monthly");
  const [service, setService] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    const params = { period };
    if (service) params.service = service;
    if (status) params.status = status;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const r = await api.get("/reports/advanced", { params });
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportFile = async (fmt) => {
    const token = localStorage.getItem("crm_token");
    const params = new URLSearchParams({ fmt });
    if (service) params.set("service", service);
    if (status) params.set("status", status);
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    const res = await fetch(`${API_BASE}/reports/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` }, credentials: "include",
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `leads_report_${new Date().toISOString().slice(0, 10)}.${fmt}`;
    a.click(); URL.revokeObjectURL(url);
  };

  const chart = (title, arr, testId) => (
    <div className="card p-5" data-testid={testId}>
      <h3 className="font-heading font-bold mb-3">{title}</h3>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <BarChart data={arr} layout="vertical" margin={{ left: 8, right: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} width={120} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Bar dataKey="value" fill="#0052CC" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Advanced Reports</h1>
        <p className="text-sm text-muted-foreground">Analyze leads across period, service, connector, employee, priority and SLA — export to Excel, PDF or CSV</p>
      </div>

      <div className="card p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Period</label>
          <select className="input mt-1" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="report-period">
            <option value="daily">Daily</option><option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option><option value="yearly">Yearly</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Service</label>
          <select className="input mt-1" value={service} onChange={(e) => setService(e.target.value)} data-testid="report-service">
            <option value="">All</option>
            {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Status</label>
          <select className="input mt-1" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="report-status">
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">From</label>
          <input type="date" className="input mt-1" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">To</label>
          <input type="date" className="input mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" />
        </div>
        <button onClick={load} className="btn btn-primary" data-testid="report-apply">Apply</button>
        <div className="flex-1" />
        <button onClick={() => exportFile("xlsx")} className="btn btn-outline" data-testid="report-export-xlsx"><FileSpreadsheet size={14} /> Excel</button>
        <button onClick={() => exportFile("pdf")} className="btn btn-outline" data-testid="report-export-pdf"><FileText size={14} /> PDF</button>
        <button onClick={() => exportFile("csv")} className="btn btn-outline" data-testid="report-export-csv"><FileType size={14} /> CSV</button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total leads</div><div className="font-heading text-3xl font-extrabold mt-2 tabular">{data.total}</div></div>
            <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Services</div><div className="font-heading text-3xl font-extrabold mt-2 tabular">{data.service_wise.length}</div></div>
            <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Connectors</div><div className="font-heading text-3xl font-extrabold mt-2 tabular">{data.connector_wise.length}</div></div>
            <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Employees w/ leads</div><div className="font-heading text-3xl font-extrabold mt-2 tabular">{data.employee_wise.length}</div></div>
          </div>

          <div className="card p-5">
            <h3 className="font-heading font-bold mb-3">By {period}</h3>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={data.period_wise}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="value" fill="#0052CC" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {chart("By Status", data.status_wise, "chart-status")}
            {chart("By Service", data.service_wise, "chart-service")}
            {chart("By Connector", data.connector_wise, "chart-connector")}
            {chart("By Employee", data.employee_wise, "chart-employee")}
            {chart("By Priority", data.priority_wise, "chart-priority")}
            {chart("By SLA", data.sla_wise, "chart-sla")}
          </div>
        </>
      )}
    </div>
  );
}
